import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, spacing, typography, shadow } from '../theme';
import { useTheme } from '../contexts/ThemeContext';
import { useThemedStyles } from '../hooks/useThemedStyles';

interface SocialLoginProps {
  onGoogleLogin?: () => void;
  onFacebookLogin?: () => void;
  onAppleLogin?: () => void;
  isLoading?: boolean;
}

export const SocialLogin: React.FC<SocialLoginProps> = ({
  onGoogleLogin,
  onFacebookLogin,
  onAppleLogin,
  isLoading = false,
}) => {
  const styles = useThemedStyles(getStyles);
  const { themeMode } = useTheme();
  // Apple's logo has no inherent brand color (unlike Google/Facebook) — it must flip to white
  // in dark mode or it's invisible against the dark surface behind it.
  const appleIconColor = themeMode === 'dark' ? '#ffffff' : '#000000';

  return (
    <View style={styles.container}>
      <View style={styles.dividerContainer}>
        <View style={styles.divider} />
        <Text style={styles.dividerText}>or continue with</Text>
        <View style={styles.divider} />
      </View>

      <View style={styles.socialButtonsContainer}>
        {onGoogleLogin && (
          <TouchableOpacity
            style={[styles.socialButton, styles.googleButton]}
            onPress={onGoogleLogin}
            disabled={isLoading}
          >
            <Ionicons name="logo-google" size={24} color="#DB4437" />
          </TouchableOpacity>
        )}

        {onFacebookLogin && (
          <TouchableOpacity
            style={[styles.socialButton, styles.facebookButton]}
            onPress={onFacebookLogin}
            disabled={isLoading}
          >
            <Ionicons name="logo-facebook" size={24} color="#4267B2" />
          </TouchableOpacity>
        )}

        {onAppleLogin && (
          <TouchableOpacity
            style={[styles.socialButton, styles.appleButton]}
            onPress={onAppleLogin}
            disabled={isLoading}
          >
            <Ionicons name="logo-apple" size={24} color={appleIconColor} />
          </TouchableOpacity>
        )}
      </View>
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    marginVertical: spacing.lg,
  },
  dividerContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.lg,
  },
  divider: {
    flex: 1,
    height: 1,
    backgroundColor: colors.border,  // Use default border color
  },
  dividerText: {
    marginHorizontal: spacing.md,
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
    fontFamily: typography.fontFamily.primary,
  },
  socialButtonsContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: spacing.md,
  },
  socialButton: {
    width: 48,
    height: 48,
    borderRadius: 24,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: colors.background.surface,
    borderWidth: 1,
    borderColor: colors.border,  // Use light border color
    shadowColor: shadow,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  googleButton: {
    backgroundColor: colors.background.surface,
  },
  facebookButton: {
    backgroundColor: colors.background.surface,
  },
  appleButton: {
    backgroundColor: colors.background.surface,
  },
});
